import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { BackendConfig } from './env'
import type { WritingsBackend } from './backend'
import type { Profile, Viewer } from './types'

interface SessionValue {
  backend: WritingsBackend
  config: BackendConfig
  viewer: Viewer | null
  profile: Profile | null
  loading: boolean
  refreshProfile: () => Promise<void>
}

const SessionContext = createContext<SessionValue | null>(null)

export function SessionProvider({
  backend,
  config,
  children,
}: {
  backend: WritingsBackend
  config: BackendConfig
  children: ReactNode
}) {
  const [viewer, setViewer] = useState<Viewer | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [loading, setLoading] = useState(true)

  const refreshProfile = useCallback(async () => {
    try {
      setProfile(await backend.getMyProfile())
    } catch {
      setProfile(null)
    }
  }, [backend])

  useEffect(() => {
    let alive = true
    backend
      .getViewer()
      .then(async (v) => {
        if (!alive) return
        setViewer(v)
        if (v) await refreshProfile()
      })
      .catch(() => alive && setViewer(null))
      .finally(() => alive && setLoading(false))
    const off = backend.onViewerChange((v) => {
      setViewer(v)
      if (v) void refreshProfile()
      else setProfile(null)
    })
    return () => {
      alive = false
      off()
    }
  }, [backend, refreshProfile])

  const value = useMemo(
    () => ({ backend, config, viewer, profile, loading, refreshProfile }),
    [backend, config, viewer, profile, loading, refreshProfile],
  )
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext)
  if (!ctx) throw new Error('useSession must be used inside <SessionProvider>')
  return ctx
}

export function useBackend(): WritingsBackend {
  return useSession().backend
}
